/**
 * Supabase Client - Re-exports the singleton browser client
 *
 * IMPORTANT: This module re-exports the singleton from lib/supabase/client.ts
 * to ensure ALL services use the SAME Supabase client instance.
 *
 * Previously this file created its own client, which caused auth issues:
 * - AuthClientService used lib/supabase/client.ts (session stored there)
 * - Other services used this file's separate client (no session = RLS fails)
 *
 * Now all services share the same singleton client with the same session.
 */

import {
  createBrowserClient,
  isSupabaseConfigured as checkConfigured,
} from "@/lib/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";

// Create a proxy that lazily gets the singleton client
// This ensures the client is only created once and shared everywhere
const getClient = (): SupabaseClient => {
  const client = createBrowserClient();
  if (!client) {
    throw new Error(
      "Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_DEFAULT_KEY (or VITE_SUPABASE_ANON_KEY).",
    );
  }
  return client;
};

/**
 * Supabase Client (Singleton)
 *
 * This is a proxy that forwards all operations to the singleton browser client.
 * This ensures the auth session is shared across all services.
 */
export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
  get(_target, prop: string | symbol) {
    const client = getClient();
    const value = client[prop as keyof SupabaseClient];
    if (typeof value === "function") {
      return (value as Function).bind(client);
    }
    return value;
  },
});

/**
 * Helper to check if Supabase is properly configured
 */
export const isSupabaseConfigured = checkConfigured;
